import React, { useState } from 'react';
import Image from '../../../components/appImage';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/button';
import StatusIndicator from '../../../components/ui/statusIndicator';
import axiosInstance from '../../../utils/axiosInstance';

const BACKEND_URL = import.meta.env.VITE_API_URL;

const EmployeePhotoCard = ({ employee, setEmployee, isAdmin = false }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [notification, setNotification] = useState(null);
  const [isEditing, setIsEditing] = useState(false); // 👈 toggle edit mode

  const positions = ['front', 'back', 'left', 'right'];
  const employeeImagesArr = employee?.images || [];

  const employeeImagesObj = {};
  positions.forEach((pos, index) => {
    employeeImagesObj[pos] = employeeImagesArr[index]
      ? `${BACKEND_URL}${employeeImagesArr[index]}`
      : '';
  });

  
  const [images, setImages] = useState(employeeImagesObj);
    
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleImageSelect = (event, position) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setSelectedFiles((prev) => ({ ...prev, [position]: file }));
    setImages((prev) => ({ ...prev, [position]: URL.createObjectURL(file) }));
  };

  const handleUpdateProfile = async ({ data = {} } = {}) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("data", JSON.stringify(data));
      console.log("data",data.department)
      ["front", "back", "left", "right"].forEach((pos, idx) => {
        if (selectedFiles[pos]) {
          formData.append("files", selectedFiles[pos]);
        } else {
          formData.append(`existing_image${idx + 1}`, employeeImagesObj[`image${idx + 1}`] || "");
        }
      });

      const response = await axiosInstance.post(
        `/employee/update-profile/${employee.employee_id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setEmployee((prev) => ({
        ...prev,
        ...response.data.employee,
        updatedAt: new Date().toISOString(),
      }));

      setIsEditing(false); // 👈 collapse back after saving
      showNotification("Profile updated successfully", "success");
    } catch (error) {
      console.error("Error updating profile:", error);
      showNotification("Failed to update profile", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'active';
      case 'inactive': return 'inactive';
      case 'suspended': return 'error';
      default: return 'inactive';
    }
  };

  const uploadSlots = [
    { key: 'front', label: 'Front' },
    { key: 'back', label: 'Back' },
    { key: 'left', label: 'Left Side' },
    { key: 'right', label: 'Right Side' }
  ];

  return (
    <>
      {/* Notification */}
      {notification && (
        <div className={`fixed top-10 right-4 z-1020 p-4 rounded-lg shadow-lg border 
          ${notification.type === 'success'
            ? 'bg-green-100 text-green-700 border-green-400'
            : notification.type === 'error'
              ? 'bg-red-100 text-red-700 border-red-400'
              : 'bg-gray-100 text-gray-700 border-gray-300'
          }`}>
          <div className="flex items-center space-x-2">
            <Icon
              name={notification.type === 'success'
                ? 'CheckCircle'
                : notification.type === 'error'
                  ? 'XCircle'
                  : 'Info'}
              size={16}
            />
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg border border-border p-6">
        {/* 👇 Image section */}
        {!isEditing ? (
          <div className="relative flex flex-col items-center">
            {/* Profile Image */}
            <div className="w-36 h-36 rounded-full overflow-hidden  shadow-md">
              <Image
                src={images?.front || `https://via.placeholder.com/150?text=Front`}
                alt="Front View"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Overlapping Edit Icon */}
            {!isAdmin && (
              <button
                onClick={() => setIsEditing(true)}
                disabled={isUploading}
                className="absolute bottom-2 right-10 bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white 
                 p-2 rounded-full shadow-md transition transform hover:scale-105"
              >
                <Icon name="Edit" size={18} />
              </button>
            )}
          </div>

        ) : (
          <>
            {/* Show all slots when editing */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {uploadSlots.map((slot) => (
                <div key={slot.key} className="relative group flex flex-col items-center">
                  <div className="w-28 h-28 rounded-full overflow-hidden  bg-gray-100 shadow-inner">
                    <Image
                      src={images[slot.key] || `https://via.placeholder.com/150?text=${slot.label}`}
                      alt={`${slot.label} View`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center w-28 h-28">
                    <label className="cursor-pointer flex flex-col items-center text-white">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageSelect(e, slot.key)}
                        className="hidden"
                        disabled={isUploading}
                      />
                      <Icon
                        name={isUploading ? "Loader2" : "Camera"}
                        size={22}
                        className={isUploading ? "animate-spin mb-1" : "mb-1"}
                      />
                      <span className="text-xs">
                        {isUploading ? "Uploading..." : `Change ${slot.label}`}
                      </span>
                    </label>
                  </div>
                  <p className="text-xs mt-2 text-gray-500 font-medium">{slot.label}</p>
                </div>
              ))}
            </div>

            {/* Save/Cancel Buttons */}
            <div className="flex space-x-3">
              <Button
                variant="solid"
                size="md"
                className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white rounded-lg flex-1 p-2 rounded"
                iconName="Upload"
                onClick={() => handleUpdateProfile({ data: employee })}
                disabled={isUploading}
              >
                Save All Images
              </Button>
              <Button
                variant="outline"
                size="md"
                className="flex-1 bg-white hover:bg-[#1e3a8a] rounded"
                onClick={() => setIsEditing(false)}
                disabled={isUploading}
              >
                Cancel
              </Button>
            </div>
          </>
        )}

        {/* Employee Info */}
        <div className="text-center mt-6">
          <h2 className="text-xl font-bold text-gray-900">
            {employee?.first_name} {employee?.last_name}
          </h2>
          <p className="text-sm text-gray-500 font-mono mt-1">
            ID: {employee?.employee_id}
          </p>
        </div>

        {/* Status */}
        <div className="flex items-center justify-center mt-3 space-x-2">
          <StatusIndicator type={getStatusColor(employee?.status)} size="sm" />
          <span
            className={`text-sm font-semibold capitalize ${employee?.status === "active"
              ? "text-green-600"
              : employee?.status === "suspended"
                ? "text-red-600"
                : "text-gray-500"
              }`}
          >
            {employee?.status}
          </span>
        </div>
        {/* Basic Info */}
        <div className="space-y-4 mb-6 mt-4">
          <div className="flex items-center space-x-3 text-sm">
            <Icon name="Mail" size={16} className="text-muted-foreground" />
            <span className="text-foreground">{employee?.email}</span>
          </div>

          <div className="flex items-center space-x-3 text-sm">
            <Icon name="Phone" size={16} className="text-muted-foreground" />
            <span className="text-foreground">{employee?.phone_number}</span>
          </div>

          <div className="flex items-center space-x-3 text-sm">
            <Icon name="Briefcase" size={16} className="text-muted-foreground" />
            <span className="text-foreground capitalize">{employee?.employee_type}</span>
          </div>

          <div className="flex items-center space-x-3 text-sm">
            <Icon name="Calendar" size={16} className="text-muted-foreground" />
            <span className="text-foreground">
              Joined {employee.date_of_join
                ? new Date(employee.date_of_join).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
                : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeePhotoCard;
